import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart3,
  CalendarDays,
  TrendingUp,
  TrendingDown,
  Activity,
  Users,
  Layers,
  Package,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Stat Card ─────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color = "text-foreground" }) {
  return (
    <div className="p-4 border rounded-lg bg-card space-y-1" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${color}`} />}
      </div>
      <p className={`text-2xl font-bold tracking-tight ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ── Efficiency Bar ────────────────────────────────────
function EfficiencyBar({ pct }) {
  const clamp = Math.min(100, Math.max(0, pct || 0));
  const color = clamp >= 90 ? "bg-emerald-500" : clamp >= 70 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${clamp}%` }} />
      </div>
      <span className="text-xs font-medium w-10 text-right">{pct}%</span>
    </div>
  );
}

export default function ProductionReportsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState([]);
  const [filterProcess, setFilterProcess] = useState("all");

  // Default date range: last 30 days
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);

  // Tab: overview | daily | process
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    axios.get(`${API}/api/production/processes`).then(r => setProcesses(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [analyticsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/production/analytics`, { params }),
        axios.get(`${API}/api/production/summary`, {
          params: { ...params, ...(filterProcess !== "all" ? { process_id: filterProcess } : {}) }
        }),
      ]);
      setAnalytics(analyticsRes.data);
      setSummary(summaryRes.data);
    } catch {
      setAnalytics(null);
      setSummary([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterProcess]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const overview = analytics?.overview || {};
  const byProcess = analytics?.by_process || [];
  const dailyTrend = analytics?.daily_trend || [];

  return (
    <div className="p-6 space-y-6" data-testid="production-reports-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl tracking-tight flex items-center gap-2">
          <BarChart3 className="h-6 w-6" /> Production Reports
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Analytics and daily summaries across production processes
        </p>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 border rounded-lg bg-card">
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1"><CalendarDays className="h-3 w-3" /> From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="report-date-from" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1"><CalendarDays className="h-3 w-3" /> To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="report-date-to" />
        </div>
        <div className="w-52">
          <Label className="text-xs text-muted-foreground mb-1 block">Process (Daily tab)</Label>
          <Select value={filterProcess} onValueChange={setFilterProcess}>
            <SelectTrigger className="h-9" data-testid="report-filter-process">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Processes</SelectItem>
              {processes.map((p) => (
                <SelectItem key={p.process_id} value={p.process_id}>{p.process_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { key: "overview", label: "Overview" },
          { key: "process", label: "By Process" },
          { key: "daily", label: "Daily Breakdown" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading analytics...</div>
      ) : (
        <>
          {/* ── Overview Tab ── */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Entries" value={overview.total_entries || 0} icon={Layers} sub={`${overview.active_processes || 0} processes active`} />
                <StatCard
                  label="Overall Efficiency"
                  value={`${overview.overall_efficiency || 0}%`}
                  icon={overview.overall_efficiency >= 85 ? TrendingUp : TrendingDown}
                  color={overview.overall_efficiency >= 85 ? "text-emerald-600" : "text-amber-600"}
                  sub="Output / Input ratio"
                />
                <StatCard
                  label="Wastage"
                  value={`${overview.overall_wastage || 0}%`}
                  icon={overview.overall_wastage <= 10 ? ArrowDownRight : ArrowUpRight}
                  color={overview.overall_wastage <= 10 ? "text-emerald-600" : "text-red-600"}
                  sub="Material loss"
                />
                <StatCard label="Total Manpower" value={overview.total_people || 0} icon={Users} sub="People deployed" />
              </div>

              {/* Quick Process Rankings */}
              <div className="border rounded-lg p-4 bg-card space-y-3">
                <h3 className="text-sm font-semibold">Process Efficiency Ranking</h3>
                {byProcess.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No data for selected period</p>
                ) : (
                  <div className="space-y-3">
                    {byProcess
                      .sort((a, b) => b.efficiency_pct - a.efficiency_pct)
                      .map((p) => (
                        <div key={p.process_id} className="flex items-center gap-3">
                          <span className="text-sm w-48 truncate font-medium">{p.process_name}</span>
                          <EfficiencyBar pct={p.efficiency_pct} />
                          <span className="text-xs text-muted-foreground w-20">{p.total_entries} entries</span>
                          <Badge variant={p.wastage_pct <= 10 ? "default" : "destructive"} className="text-xs">
                            {p.wastage_pct}% waste
                          </Badge>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Input vs Output totals */}
              <div className="grid grid-cols-2 gap-4">
                <StatCard label="Total Input" value={(overview.total_input || 0).toLocaleString()} icon={Package} sub="Across all processes" />
                <StatCard label="Total Output" value={(overview.total_output || 0).toLocaleString()} icon={Activity} sub="Across all processes" color="text-emerald-600" />
              </div>
            </div>
          )}

          {/* ── By Process Tab ── */}
          {activeTab === "process" && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Process</TableHead>
                    <TableHead className="text-right">Entries</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Operators</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Input</TableHead>
                    <TableHead className="text-right">Output</TableHead>
                    <TableHead className="text-right">Manpower</TableHead>
                    <TableHead>Efficiency</TableHead>
                    <TableHead className="text-right">Wastage</TableHead>
                    <TableHead className="text-right">Avg/Hr</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byProcess.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No data</TableCell></TableRow>
                  ) : (
                    byProcess.map((p) => (
                      <TableRow key={p.process_id} data-testid={`process-row-${p.process_id}`}>
                        <TableCell className="font-medium text-sm">{p.process_name}</TableCell>
                        <TableCell className="text-right text-sm">{p.total_entries}</TableCell>
                        <TableCell className="text-right text-sm">{p.unique_orders}</TableCell>
                        <TableCell className="text-right text-sm">{p.unique_operators}</TableCell>
                        <TableCell className="text-right text-sm">{p.total_planned.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm">{p.total_input.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm">{p.total_output.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm">{p.total_people}</TableCell>
                        <TableCell><EfficiencyBar pct={p.efficiency_pct} /></TableCell>
                        <TableCell className="text-right">
                          <Badge variant={p.wastage_pct <= 10 ? "outline" : "destructive"} className="text-xs">{p.wastage_pct}%</Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">{p.avg_prod_hr}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* ── Daily Breakdown Tab ── */}
          {activeTab === "daily" && (
            <div className="space-y-4">
              {filterProcess !== "all" && (
                <Badge variant="secondary">{processes.find(p => p.process_id === filterProcess)?.process_name}</Badge>
              )}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Date</TableHead>
                      <TableHead>Process</TableHead>
                      <TableHead className="text-right">Entries</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right">Input</TableHead>
                      <TableHead className="text-right">Output</TableHead>
                      <TableHead className="text-right">Manpower</TableHead>
                      <TableHead>Efficiency</TableHead>
                      <TableHead className="text-right">Wastage</TableHead>
                      <TableHead className="text-right">Avg/Hr</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.length === 0 ? (
                      <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No data for selected period</TableCell></TableRow>
                    ) : (
                      summary.map((s, idx) => (
                        <TableRow key={`${s.production_date}-${s.process_id}-${idx}`} data-testid={`daily-row-${idx}`}>
                          <TableCell className="text-sm font-medium">{formatDate(s.production_date)}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{s.process_name}</Badge></TableCell>
                          <TableCell className="text-right text-sm">{s.total_entries}</TableCell>
                          <TableCell className="text-right text-sm">{s.total_planned_qty?.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-sm">{s.total_input_qty?.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-sm">{s.total_output_qty?.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-sm">{s.total_people}</TableCell>
                          <TableCell><EfficiencyBar pct={s.efficiency_pct} /></TableCell>
                          <TableCell className="text-right">
                            <Badge variant={s.wastage_pct <= 10 ? "outline" : "destructive"} className="text-xs">{s.wastage_pct}%</Badge>
                          </TableCell>
                          <TableCell className="text-right text-sm">{s.avg_production_per_hr}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
